"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  InstagramIcon,
  XIcon,
  TikTokIcon,
  ThreadsIcon,
} from "@/components/icons/social";
import { getSocialUrl } from "@/lib/config/social-links";
import { useTranslations, useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import { keywordScopeHref } from "@/lib/search/search-url";
import { LanguageSwitcher } from "./LanguageSwitcher";

type FooterVariant = "full" | "minimal";

/**
 * 푸터 "둘러보기" 열에 노출하는 카테고리.
 *
 * `src/lib/constants/seo.ts` 의 `INDEXABLE_CATEGORY_CODES` 와 **짝을 이루는 목록**이다
 * (색인 승격 카테고리 = 푸터 노출 카테고리). 색인 대상을 늘리거나 빼면 이 배열도 같이
 * 고쳐야 sitemap·canonical 로 올라간 카테고리 랜딩에 전역 내부링크가 걸린다.
 * href 는 `keywordScopeHref` 로 만들어 sitemap·canonical 과 동일한 정규형
 * (`/{locale}/s/products?scope=keyword&categories=CODE`)을 유지한다.
 */
const FOOTER_CATEGORIES = [
  { code: "20-01", labelKey: "catSmartphone" },
  { code: "10-06-01", labelKey: "catLightStick" },
  { code: "10-05-01", labelKey: "catTraditionalWear" },
  { code: "50-01-03", labelKey: "catBike" },
  { code: "10-04", labelKey: "catCamping" },
] as const;

export default function Footer() {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("common.footer");

  // Remove locale prefix for path checking
  const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";

  // 검색 페이지(/s/)에서 모바일일 때 푸터 숨김
  const isSearchPage = pathWithoutLocale.startsWith("/s/");

  // 계정 설정 페이지인지 확인 (푸터 숨김)
  const isAccountSettingsPage =
    pathWithoutLocale.startsWith("/account-settings");

  // 프로필 페이지인지 확인
  const isProfilePage =
    pathWithoutLocale.startsWith("/profile") && !isAccountSettingsPage;

  // 예약 상세 페이지인지 확인 (푸터 숨김)
  const isReservationDetailPage = /^\/reservations\/[^/]+$/.test(
    pathWithoutLocale,
  );

  // Hide footer on auth pages (always)
  // `/crew/*` 는 자체 크루 푸터(공개 페이지) 또는 푸터 없음(콘솔)을 쓴다.
  const authPaths = ["/login", "/signup", "/auth", "/messages", "/crew"];
  const shouldHideAlways = authPaths.some(
    (path) =>
      pathWithoutLocale === path || pathWithoutLocale.startsWith(path + "/"),
  );

  // 앱 설치·이어가기 페이지(/app)는 이동용 화면이라 푸터를 두지 않는다.
  const isAppInstallPage = pathWithoutLocale === "/app";

  // 항상 숨겨야 하는 페이지 (auth 페이지, 계정 설정 페이지, 예약 상세 페이지, 앱 설치)
  if (
    shouldHideAlways ||
    isAccountSettingsPage ||
    isReservationDetailPage ||
    isAppInstallPage
  ) {
    return null;
  }

  // 검색 페이지와 프로필 페이지에서는 모바일에서 숨김 (Tailwind로 처리)
  const hideOnMobile = isSearchPage || isProfilePage;

  // 간소화된 푸터를 표시할 페이지 (상품 상세, 결제 플로우)
  const minimalFooterPaths = ["/products/", "/p/", "/checkout"];
  const variant: FooterVariant = minimalFooterPaths.some((path) =>
    pathWithoutLocale.startsWith(path),
  )
    ? "minimal"
    : "full";

  return (
    <footer className={cn("bg-gray-100", hideOnMobile && "hidden md:block")}>
      <div
        className={cn(
          "max-w-480 mx-auto px-4 sm:px-6 lg:px-8 pt-8",
          // minimal 페이지(상품 상세·결제)는 lg 미만에서 하단 고정 CTA 바가 표시되며,
          // 보증금 안내가 붙으면 바 높이가 ~140px까지 커지므로 그만큼 클리어런스 필요
          variant === "minimal"
            ? "sm:pt-12 pb-[calc(10rem+var(--loc-safe-area-inset-bottom))] lg:pb-12"
            : "pb-[calc(6rem+var(--loc-safe-area-inset-bottom))] sm:py-12",
        )}
      >
        {/* Top Section - Company Info & Links (Full Footer Only) */}
        {variant === "full" && (
          <div className="flex flex-col lg:flex-row justify-between gap-8 mb-8">
            {/* Company Info */}
            <div className="lg:w-auto">
              <h3 className="font-bold text-lg mb-4">{t("customerSupport")}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>{t("chatSupport")}</p>
                <p>{t("phoneSupport")}</p>
                <p>{t("phoneNumber")}</p>
              </div>
              <Link
                href={`/${locale}/help`}
                className="mt-4 inline-block px-6 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                {t("contact")}
              </Link>
            </div>

            {/* Links Section */}
            <div className="lg:w-[62%] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Explore */}
              <div>
                <h3 className="font-bold text-lg mb-4">{t("explore")}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      href={`/${locale}/events`}
                      className="hover:text-gray-900"
                    >
                      {t("events")}
                    </Link>
                  </li>
                  {FOOTER_CATEGORIES.map(({ code, labelKey }) => (
                    <li key={code}>
                      <Link
                        href={keywordScopeHref(locale, { categories: code })}
                        className="hover:text-gray-900"
                      >
                        {t(labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* About */}
              <div>
                <h3 className="font-bold text-lg mb-4">{t("about")}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      href={`/${locale}/about`}
                      className="hover:text-gray-900"
                    >
                      {t("company")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${locale}/careers`}
                      className="hover:text-gray-900"
                    >
                      {t("careers")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${locale}/sustainability`}
                      className="hover:text-gray-900"
                    >
                      {t("sustainability")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${locale}/blog`}
                      className="hover:text-gray-900"
                    >
                      {t("blog")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Partner */}
              <div>
                <h3 className="font-bold text-lg mb-4">{t("partner")}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      href={`https://partner.locarora.com/${locale}`}
                      className="hover:text-gray-900"
                    >
                      {t("becomePartner")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`https://partner.locarora.com/${locale}`}
                      className="hover:text-gray-900"
                    >
                      {t("partnerInquiry")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`https://partner.locarora.com/${locale}`}
                      className="hover:text-gray-900"
                    >
                      {t("partnerRecommend")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Support */}
              <div>
                <h3 className="font-bold text-lg mb-4">{t("support")}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      href={`/${locale}/help`}
                      className="hover:text-gray-900"
                    >
                      {t("notice")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/${locale}/help/faq`}
                      className="hover:text-gray-900"
                    >
                      {t("faq")}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Middle Section - Links & Social */}
        <div
          className={cn(
            "flex flex-col-reverse md:flex-row justify-between items-start md:items-center gap-4 pb-6",
            variant === "full" && "pt-6 border-t border-gray-200",
          )}
        >
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <Link
              href={`/${locale}/help/terms`}
              className="hover:text-gray-900"
            >
              {t("terms")}
            </Link>
            <Link
              href={`/${locale}/help/privacy`}
              className="font-semibold hover:text-gray-900"
            >
              {t("privacy")}
            </Link>
            {variant === "full" && (
              <>
                <Link
                  href={`/${locale}/help/location`}
                  className="hover:text-gray-900"
                >
                  {t("locationTerms")}
                </Link>
                <Link
                  href={`/${locale}/help/refund`}
                  className="hover:text-gray-900"
                >
                  {t("refundPolicy")}
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-4">
            <LanguageSwitcher
              variant="ghost"
              showLabel={true}
              className="text-gray-900 bg-transparent hover:bg-gray-200"
            />
            <div className="flex items-center gap-1">
            <Link
              href={getSocialUrl("instagram", locale)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              <InstagramIcon className="h-5 w-5" />
            </Link>
            <Link
              href={getSocialUrl("x", locale)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              <XIcon className="h-5 w-5" />
            </Link>
            <Link
              href={getSocialUrl("tiktok", locale)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              <TikTokIcon className="h-5 w-5" />
            </Link>
            <Link
              href={getSocialUrl("threads", locale)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Threads"
              className="flex items-center justify-center w-10 h-10 rounded-full text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              <ThreadsIcon className="h-5 w-5" />
            </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section - Company Details */}
        <div className="text-xs text-gray-500 space-y-2">
          {/* US entity first, then the Korean entity (separated by spacing only) */}
          <p>
            <span>{t("usCompanyInfo")}</span>
            {" | "}
            <span>{t("usAddress")}</span>
          </p>
          <p className="mt-3">
            <span>{t("companyInfo")}</span>
            {" | "}
            <span>{t("license")}</span>
          </p>
          <p>{t("address")}</p>
          {variant === "full" && (
            <>
              <p className="mt-4">{t("disclaimer")}</p>
              <p>{t("inquiry")}</p>
            </>
          )}
          <p className={variant === "minimal" ? "mt-2" : "mt-4"}>
            {t("copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
